import type { Exam, ExamState } from '../../exam.js';
import { resolveIsGeneratedExam } from '../../utils/exam-origin.js';
import { resolveIsStudentOwnedExam } from '../../utils/exam-role.js';

export const openCanvasToolDef = [
  {
    type: 'function',
    function: {
      name: 'open_canvas',
      description:
        'Opens the appropriate canvas in the UI. Use this tool when it is helpful in the context of the conversation to display tasks, grading, master solutions, exam structure, grading criteria, uncertainties, or summaries. ALSO use this tool IMMEDIATELY when the user explicitly asks to view these elements, OR when the user wants to test the system with sample data / demo exams. ALSO use this tool with canvas "legal" IMMEDIATELY whenever the user raises a legal or regulatory topic (e.g. laws, statutes, compliance, data protection, school or exam law, AI regulation).',
      parameters: {
        type: 'object',
        properties: {
          canvas: {
            type: 'string',
            enum: [
              'correction',
              'answers',
              'msedit',
              'summary',
              'structureeditor',
              'submissioncheck',
              'demoselector',
              'examcreator',
              'legal',
            ],
            description:
              'Which canvas to open. ' +
              '"correction": for correcting/grading student answers and checking AI evaluations/uncertainties (grading mode). ' +
              '"answers": for viewing/reviewing the extracted student answers only, without the AI grading/correction focus (answer review mode). ' +
              '"msedit": for anything related to master solutions and grading schemes/criteria. ' +
              '"summary": for results, summaries, or grade overviews. ' +
              '"structureeditor": to view or edit the exam structure and task regions. ' +
              '"submissioncheck": to view or check page-to-student assignments and submissions. ' +
              '"demoselector": to open the demo/sample data selection when the user wants to try out the system without uploading their own files. ' +
              '"examcreator": to view or edit a self-created (not uploaded) exam — its tasks, points, figures and layout — e.g. when the user asks to see or change the exam that was just generated. ' +
              '"legal": the Legal Navigator, an interactive legal/regulatory assistant. Open it as soon as the user asks about or mentions legal or regulatory matters.',
          },
        },
        required: ['canvas'],
      },
    },
  },
];

export async function handleOpenCanvas(exam: Exam, toolCallArgs: any): Promise<object> {
  const canvasToOpen = toolCallArgs.canvas;

  // A student only ever uploads their own single exam, so there's never a page-to-student
  // assignment to review — the canvas is teacher-only.
  if (canvasToOpen === 'submissioncheck' && (await resolveIsStudentOwnedExam((exam as any)?._id))) {
    return { content: 'SYSTEM INFO: Diese Ansicht ist für Schüler nicht verfügbar.' };
  }

  // The Legal Navigator embeds a third-party ChatGPT app and is not offered to students (minors).
  if (canvasToOpen === 'legal' && (await resolveIsStudentOwnedExam((exam as any)?._id))) {
    return { content: 'SYSTEM INFO: Diese Ansicht ist für Schüler nicht verfügbar.' };
  }

  // A generated exam's structure was never read off a scan — there is no structure-editor pass, and no
  // regions to review — the exam-creator canvas is its editor instead (see structureJob.ready's own
  // generatedExam drop).
  if (canvasToOpen === 'structureeditor' && (await resolveIsGeneratedExam((exam as any)?._id))) {
    return { content: 'SYSTEM INFO: Diese Ansicht ist für diese Klausur nicht verfügbar. Öffne stattdessen "examcreator".' };
  }

  const toolResultContent = `SYSTEM INFO: Canvas "${toolCallArgs.canvas}" geöffnet.`;
  return { content: toolResultContent, canvasToOpen };
}

export function openCanvasAvailability(_examState: ExamState): boolean {
  return true;
}
